import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { OnboardingItemStatus } from './types';

export interface AssignableTrainingModule {
  id: string;
  title: string;
  assigned: boolean;
  status: OnboardingItemStatus;
  published: boolean;
}

/** Which training modules a specific person is (or isn't) assigned. */
export function usePersonTrainingModules(personId: string) {
  return useQuery({
    queryKey: ['person-training-modules', personId],
    queryFn: async () =>
      (
        await api<{ training_modules: AssignableTrainingModule[] }>(
          `/api/admin/onboarding/people/${personId}/training-modules`,
        )
      ).training_modules,
  });
}

export function useAssignTrainingModule(personId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ moduleId, assigned }: { moduleId: string; assigned: boolean }) =>
      api(`/api/admin/onboarding/people/${personId}/training-modules/${moduleId}`, {
        method: assigned ? 'POST' : 'DELETE',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['person-training-modules', personId] });
      qc.invalidateQueries({ queryKey: ['person-onboarding', personId] });
      qc.invalidateQueries({ queryKey: ['admin', 'training-progress'] });
    },
  });
}

/** Reopening voids the person's prior attempts server-side (retake required). */
export function useReopenTrainingModule(personId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (moduleId: string) =>
      api(`/api/admin/onboarding/people/${personId}/training-modules/${moduleId}/reopen`, {
        method: 'POST',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['person-training-modules', personId] });
      qc.invalidateQueries({ queryKey: ['person-onboarding', personId] });
      qc.invalidateQueries({ queryKey: ['admin', 'training-progress'] });
    },
  });
}

/**
 * Keep this person off "available to everyone" training (a generic / back-office
 * account). The sweep skips them so it sticks across runs; excluding also drops
 * any still-outstanding "everyone" task now.
 */
export function useSetTrainingAssignAllExcluded(personId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (excluded: boolean) =>
      api<{ excluded: boolean; removed: number }>(
        `/api/admin/onboarding/people/${personId}/training-assign-all-exclude`,
        { method: 'POST', body: JSON.stringify({ excluded }) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['person-training-modules', personId] });
      qc.invalidateQueries({ queryKey: ['person-onboarding', personId] });
      qc.invalidateQueries({ queryKey: ['admin', 'training-progress'] });
    },
  });
}
